/**
 * InquiryPage — I-2-1. 1:1 문의하기
 *
 * 좌: 문의 등록 폼 (유형 칩 · 제목 · 내용 · 이미지 슬롯)
 * 우: 내 문의 내역 패널 (상태 배지 · 문의 상세 이동)
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Input, Textarea } from '@indipia/ui-core';
import { useAppStore, useAuthStore } from '@indipia/shared';
import { fetchInquiries, createInquiry } from '../api/inquiries';
import type { InquiryListItem } from '../api/inquiries';
import { uploadSupportFile } from '../api/files';

// ── 상태 배지 스타일 ─────────────────────────────────────────────────────────

function inquiryStatusStyle(status: string | null): React.CSSProperties {
  switch (status) {
    case 'DONE':
      return {
        color: 'var(--color-success)',
        background: 'color-mix(in srgb, var(--color-success) 12%, transparent)',
      };
    case 'IN_PROGRESS':
      return {
        color: 'var(--color-warning)',
        background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
      };
    default:
      return {
        color: 'var(--color-info)',
        background: 'color-mix(in srgb, var(--color-info) 12%, transparent)',
      };
  }
}

function inquiryStatusLabel(item: InquiryListItem): string {
  if (item.statusName) return item.statusName;
  switch (item.inquiryStatus) {
    case 'DONE': return '답변완료';
    case 'IN_PROGRESS': return '처리중';
    default: return '접수됨';
  }
}

function inquiryStatusIcon(status: string | null): string {
  switch (status) {
    case 'DONE': return '✓';
    case 'IN_PROGRESS': return '●';
    default: return '○';
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  return `${dt.getFullYear()}.${String(dt.getMonth() + 1).padStart(2, '0')}.${String(dt.getDate()).padStart(2, '0')}`;
}

// ── 문의 내역 카드 ────────────────────────────────────────────────────────────

function InquiryHistCard({ inquiry, onTap }: { inquiry: InquiryListItem; onTap: () => void }) {
  const st = inquiryStatusStyle(inquiry.inquiryStatus);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onTap}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(); } }}
      className="cursor-pointer border-b px-4 py-3.5 transition-colors last:border-b-0 hover:bg-surface"
      style={{ borderColor: 'var(--shell-border)' }}
    >
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {inquiry.categoryName && (
            <Badge
              style={{
                color: 'var(--gt-primary)',
                background: 'color-mix(in srgb, var(--gt-primary) 10%, transparent)',
                border: '1px solid color-mix(in srgb, var(--gt-primary) 20%, transparent)',
              }}
            >
              {inquiry.categoryName}
            </Badge>
          )}
        </div>
        <span className="text-xs font-bold" style={st}>
          {inquiryStatusIcon(inquiry.inquiryStatus)} {inquiryStatusLabel(inquiry)}
        </span>
      </div>
      <p className="truncate text-xs font-semibold text-text">{inquiry.caseTitle}</p>
      <p className="mt-0.5 text-xs text-text-muted">{formatDate(inquiry.createdAt)}</p>
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

const MAX_CONTENT = 1000;

export function InquiryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 로그인 회원 이름 — support가 스냅샷으로 저장하도록 payload에 실어 보낸다.
  const memberName = useAuthStore((s) => s.user?.memberName ?? '');

  // ZEU_INQUIRY_CATEGORY 코드
  const allCodes = useAppStore((s) => s.codes);
  const categories = useMemo(
    () => allCodes.filter((c) => c.codeGroupCode === 'ZEU_INQUIRY_CATEGORY'),
    [allCodes],
  );

  // 폼 상태
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  // 이미지 첨부
  const MAX_IMAGES = 3;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [maxWarning, setMaxWarning] = useState(false);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const filtered = Array.from(e.target.files ?? [])
      .filter((f) => f.type === 'image/jpeg' || f.type === 'image/png');
    const remaining = MAX_IMAGES - images.length;
    const incoming = filtered.slice(0, remaining);
    if (filtered.length > remaining) setMaxWarning(true);
    if (incoming.length === 0) return;
    setImages((prev) => [...prev, ...incoming]);
    setPreviews((prev) => [...prev, ...incoming.map((f) => URL.createObjectURL(f))]);
    e.target.value = '';
  }

  function removeImage(idx: number) {
    URL.revokeObjectURL(previews[idx]);
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  }

  // 미리보기 URL 정리
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), []);

  const canSubmit = selectedCode !== null && title.trim() !== '' && content.trim() !== '';

  // 내 문의 목록
  const { data: inquiries = [] } = useQuery({
    queryKey: ['inquiries'],
    queryFn: fetchInquiries,
  });

  // 문의 등록
  const submitMutation = useMutation({
    mutationFn: async () => {
      const uploaded = await Promise.all(
        images.map((file) => uploadSupportFile(file)),
      );
      const fileMapIds = uploaded.map((f) => f.fileMapId);
      return createInquiry({
        categoryCode: selectedCode!,
        caseTitle: title.trim(),
        caseContent: content.trim(),
        memberName,
        ...(fileMapIds.length > 0 && { fileMapIds }),
      });
    },
    onSuccess: () => {
      toast.success('1대1 문의가 접수되었습니다. 나의 문의 내역에서 확인 할 수 있습니다.');
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      setSelectedCode(null);
      setTitle('');
      setContent('');
      previews.forEach(URL.revokeObjectURL);
      setImages([]);
      setPreviews([]);
    },
  });

  function handleSubmit() {
    if (!canSubmit || submitMutation.isPending) return;
    submitMutation.mutate();
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="px-20 py-8 pb-16">

        {/* 헤더 */}
        <div className="mb-7">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--gt-primary)' }}>
            고객센터
          </p>
          <h1 className="mb-1 text-xl font-extrabold tracking-tight text-text">1:1 문의하기</h1>
          <p className="text-xs text-text-muted">궁금한 점, 불편 사항을 남겨주시면 신속하게 답변드립니다</p>
        </div>

        {/* 좌우 레이아웃 */}
        <div className="flex items-start gap-6">

          {/* 좌: 문의 폼 */}
          <div className="min-w-0 flex-1">
            <div className="rounded-xl border p-7" style={{ background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' }}>

              {/* 문의 유형 */}
              <div className="mb-5">
                <label className="mb-2.5 block text-xs font-bold" style={{ color: 'var(--shell-on-surface)' }}>
                  문의 유형 <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <Button
                      key={c.codeValue}
                      variant={selectedCode === c.codeValue ? 'ghost' : 'secondary'}
                      size="sm"
                      onClick={() => setSelectedCode(c.codeValue)}
                      className="!px-4 !py-1.5 !text-xs !font-semibold !transition-all"
                      style={
                        selectedCode === c.codeValue
                          ? { background: 'var(--gt-primary)', borderColor: 'var(--gt-primary)', color: 'var(--gt-on-primary)' }
                          : undefined
                      }
                    >
                      {c.codeName}
                    </Button>
                  ))}
                </div>
              </div>

              {/* 문의 제목 */}
              <div className="mb-5">
                <label className="mb-2.5 block text-xs font-bold" style={{ color: 'var(--shell-on-surface)' }}>
                  문의 제목 <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="문의 내용을 요약해서 입력해 주세요"
                  maxLength={200}
                />
              </div>

              {/* 문의 내용 */}
              <div className="mb-5">
                <label className="mb-2.5 block text-xs font-bold" style={{ color: 'var(--shell-on-surface)' }}>
                  문의 내용 <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value.slice(0, MAX_CONTENT))}
                  placeholder="문의하실 내용을 상세히 입력해 주세요. 오류 발생 시 발생 시간, 사용 기기, 재현 방법 등을 함께 작성해 주시면 빠른 처리에 도움이 됩니다."
                  rows={7}
                  className="!max-w-none"
                />
                <p className="mt-1 text-right text-xs" style={{ color: 'var(--shell-on-surface-2)' }}>
                  {content.length} / {MAX_CONTENT}자
                </p>
              </div>

              {/* 이미지 첨부 */}
              <div className="mb-6">
                <label className="mb-2.5 block text-xs font-bold" style={{ color: 'var(--shell-on-surface)' }}>
                  이미지 첨부{' '}
                  <span className="text-xs font-normal text-text-muted">(최대 3장 · JPG / PNG)</span>
                </label>

                {/* 숨김 파일 input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />

                <div className="flex items-center gap-2.5">
                  {/* 업로드된 이미지 썸네일 */}
                  {previews.map((src, i) => (
                    <div key={i} className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-border">
                      <img src={src} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        aria-label="첨부 이미지 삭제"
                        className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--overlay-scrim)] text-white transition-opacity hover:bg-[var(--overlay-scrim-strong)]"
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  ))}

                  {/* 사진 추가 버튼 (3장 미만일 때만 표시) */}
                  {images.length < MAX_IMAGES && (
                    <button
                      type="button"
                      onClick={() => { setMaxWarning(false); fileInputRef.current?.click(); }}
                      className="flex h-20 w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border bg-surface text-text-muted transition-colors hover:border-group hover:text-group"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <span className="text-xs">사진 추가</span>
                    </button>
                  )}
                </div>
                {maxWarning && (
                  <p className="mt-1.5 text-xs text-text-muted">
                    이미지는 최대 3장까지 첨부할 수 있어요.
                  </p>
                )}
              </div>

              {/* 제출 버튼 */}
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || submitMutation.isPending}
                className="w-full"
              >
                {submitMutation.isPending ? '제출 중…' : '문의 제출하기'}
              </Button>

              {submitMutation.isError && (
                <p className="mt-3 text-center text-xs font-semibold" style={{ color: 'var(--color-error)' }}>
                  문의 접수에 실패했어요. 잠시 후 다시 시도해 주세요.
                </p>
              )}
            </div>
          </div>

          {/* 우: 내 문의 내역 + 안내 */}
          <div className="w-64 shrink-0">

            {/* 내 문의 내역 */}
            <div className="overflow-hidden rounded-xl border" style={{ background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' }}>
              <div className="flex items-center justify-between border-b px-4 py-3.5" style={{ borderColor: 'var(--shell-border)' }}>
                <span className="text-xs font-bold text-text">내 문의 내역</span>
                <span className="text-xs text-text-muted">{inquiries.length}건</span>
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                {inquiries.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <p className="text-xs text-text-muted">문의 내역이 없어요</p>
                  </div>
                ) : (
                  inquiries.map((q) => (
                    <InquiryHistCard
                      key={q.inquiryId}
                      inquiry={q}
                      onTap={() => navigate(`/cs/inquiry/${q.inquiryId}`)}
                    />
                  ))
                )}
              </div>
            </div>

            {/* 안내 카드 */}
            <div
              className="mt-3.5 rounded-xl border p-4"
              style={{
                background: 'color-mix(in srgb, var(--gt-primary) 4%, var(--shell-surface))',
                borderColor: 'color-mix(in srgb, var(--gt-primary) 20%, transparent)',
              }}
            >
              <p className="mb-2.5 text-xs font-bold" style={{ color: 'var(--gt-primary)' }}>문의 전 확인해 보세요</p>
              {[
                '자주 묻는 질문에서 빠른 답변을 찾아보세요',
                '답변 평균 소요 시간: 영업일 기준 24시간 이내',
                '화면 캡처를 첨부하면 더 빠른 처리가 가능해요',
              ].map((text, i) => (
                <div key={i} className="mb-1.5 flex items-start gap-2 last:mb-0">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span className="text-xs leading-relaxed" style={{ color: 'var(--shell-on-surface-2)' }}>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
